import { clsx } from 'clsx'
import { CalendarDays, ClipboardList, Mail, Settings, UserCog, Users } from 'lucide-react'
import { useIsFetching, useIsMutating } from '@tanstack/react-query'
import { useState } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { useMe } from '../../api/hooks'
import { isAdminRole, isManagerRole } from '../../lib/format'
import { Logo } from './Logo'
import { TopBar } from './TopBar'

const NAV = [
  { to: '/', label: 'Event', icon: CalendarDays, end: true },
  { to: '/team', label: 'Team & kunder', icon: Users },
  { to: '/utskick', label: 'Skickade mejl', icon: Mail, managerOnly: true },
  { to: '/logg', label: 'Aktivitetslogg', icon: ClipboardList, managerOnly: true },
  { to: '/anvandare', label: 'Användare', icon: UserCog, adminOnly: true },
  { to: '/installningar', label: 'Inställningar', icon: Settings, adminOnly: true },
] as { to: string; label: string; icon: typeof CalendarDays; end?: boolean; managerOnly?: boolean; adminOnly?: boolean }[]

export const NAV_ITEMS = NAV

/** 1 px navy bar at the top while anything is loading – the design system's loading state. */
function TopLoader() {
  const busy = useIsFetching() + useIsMutating() > 0
  return (
    <div className={clsx('pointer-events-none fixed inset-x-0 top-0 z-50 h-0.5 overflow-hidden transition-opacity duration-[220ms]', busy ? 'opacity-100' : 'opacity-0')} aria-hidden>
      <div className="h-full w-2/5 bg-navy-800 animate-loader" />
    </div>
  )
}

export function AppShell() {
  const location = useLocation()
  // Re-animate on route change, but not when only the tab segment changes (/event/:id/:tab).
  const pageKey = location.pathname.split('/').slice(0, 3).join('/')
  const me = useMe()
  const isManager = isManagerRole(me.data?.role)
  const isAdmin = isAdminRole(me.data?.role)
  const [drawer, setDrawer] = useState(false)
  const items = NAV.filter((n) => (!n.managerOnly || isManager) && (!n.adminOnly || isAdmin))
  return (
    <div className="flex min-h-dvh">
      <TopLoader />
      {drawer && (
        <div className="fixed inset-0 z-40 lg:hidden animate-fade-in" onClick={() => setDrawer(false)}>
          <div className="absolute inset-0 bg-ink-900/40" />
          <nav className="absolute inset-y-0 left-0 flex w-64 flex-col bg-navy-900 p-4 text-white animate-slide-in-left" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 border-b border-white/8 px-1 pb-4 pt-2"><Logo light /></div>
            {items.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setDrawer(false)} className={({ isActive }) => clsx('flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium', isActive ? 'bg-[rgba(74,139,194,0.18)] text-white shadow-[inset_2px_0_0_#4A8BC2]' : 'text-white/78 hover:bg-white/6 hover:text-white')}>
                <n.icon className="size-4" />{n.label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
      <aside className="sticky top-0 hidden h-dvh w-60 shrink-0 flex-col bg-navy-900 text-white lg:flex">
        <div className="mx-4 mb-3 border-b border-white/8 px-1 pb-4 pt-6">
          <Logo light />
        </div>
        <nav className="flex-1 space-y-0.5 px-3">
          {items.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) =>
                clsx(
                  'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors duration-[140ms]',
                  isActive ? 'bg-[rgba(74,139,194,0.18)] text-white shadow-[inset_2px_0_0_#4A8BC2]' : 'text-white/78 hover:bg-white/6 hover:text-white',
                )
              }
            >
              <n.icon className="size-4 opacity-85 transition-opacity group-hover:opacity-100" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="mx-4 border-t border-white/8 px-1 py-4 text-[11px] leading-relaxed text-white/45">
          Pilot v0.1 · Ersätter Trippus &amp; Apsis
        </div>
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar onMenu={() => setDrawer(true)} />
        <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-6 sm:px-6 lg:px-8">
          <div key={pageKey} className="animate-fade-in">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  )
}
